
import { useState, useEffect, useRef } from "react";

interface ExpandableDescriptionProps {
  description: string;
}

const ExpandableDescription = ({ description }: ExpandableDescriptionProps) => {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);
  
  // Check if the text is overflowing when component mounts or when window resizes
  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        const isTextOverflowing = 
          textRef.current.scrollHeight > textRef.current.clientHeight;
        setIsOverflowing(isTextOverflowing);
      }
    };
    
    checkOverflow();
    
    // Add resize listener to check overflow when window size changes
    window.addEventListener('resize', checkOverflow);
    
    // Force check after a short delay to ensure accurate calculation
    const timer = setTimeout(checkOverflow, 100);
    
    return () => {
      window.removeEventListener('resize', checkOverflow);
      clearTimeout(timer);
    };
  }, [description, expanded]);
  
  // Function to toggle expanded state
  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <div className="flex flex-col">
      <p 
        ref={textRef}
        className={`text-sm text-muted-foreground mb-1 ${expanded ? "" : "line-clamp-3"}`}
      >
        {description}
      </p>
      
      {/* Always show the button if the content is clipped or expanded */}
      {(isOverflowing || expanded) && (
        <button
          onClick={toggleExpanded}
          className="text-xs text-primary mb-3 hover:underline self-start"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
};

export default ExpandableDescription;
